// Project API Layer - TypeScript handles all validation
// ✅ No runtime validation needed - TypeScript ensures type safety

import { ProjectEvents, ProjectEndpoints } from "@generated/project";
import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { EventBus } from "src/platform/events/EventBus";

import {
  Project,
  ProjectListResponse,
  Application,
  ApplicationListResponse,
  QuickStartApplication,
  QuickStartApplicationsResponse,
  CreateProjectRequest,
  UpdateProjectRequest,
  ProjectApiError,
} from "../types/project.types";

import type { IProjectApi } from "./IProjectApi";

/**
 * Project API error codes - compile-time validation
 */
const PROJECT_ERROR_CODES = {
  PROJECT_NOT_FOUND: "PROJECT_NOT_FOUND",
  PROJECT_ALREADY_EXISTS: "PROJECT_ALREADY_EXISTS",
  INVALID_PROJECT_DATA: "INVALID_PROJECT_DATA",
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  INTERNAL_ERROR: "INTERNAL_ERROR",
  NETWORK_ERROR: "NETWORK_ERROR",
} as const;

/**
 * Project API class - TypeScript handles all validation
 */
export class ProjectApi implements IProjectApi {
  private eventBus: EventBus;
  private baseUrl: string;

  constructor(eventBus: EventBus, baseUrl: string = apiBaseUrls.BACKEND) {
    this.eventBus = eventBus;
    this.baseUrl = baseUrl;
  }

  /**
   * Get project by alias - TypeScript ensures type safety
   */
  async getProject(alias: string): Promise<Project> {
    try {
      const response = await this.makeRequest<Project>(
        ProjectEndpoints.GET_PROJECT.replace(":id", alias),
        "GET",
      );

      return response;
    } catch (error) {
      this.handleApiError(error, "Failed to load project");
      throw error;
    }
  }

  /**
   * Get projects list with pagination - TypeScript ensures type safety
   */
  async getProjects(
    page: number = 1,
    limit: number = 10,
  ): Promise<ProjectListResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      const response = await this.makeRequest<Project[]>(
        `${ProjectEndpoints.LIST_PROJECTS}?${params}`,
        "GET",
      );

      return {
        projects: response,
        total: response.length, // This would come from API pagination metadata
        page,
        limit,
      };
    } catch (error) {
      this.handleApiError(error, "Failed to load projects");
      throw error;
    }
  }

  /**
   * Create project - TypeScript ensures request type safety
   */
  async createProject(request: CreateProjectRequest): Promise<Project> {
    try {
      const response = await this.makeRequest<Project>(
        ProjectEndpoints.LIST_PROJECTS,
        "POST",
        request,
      );

      return response;
    } catch (error) {
      this.handleApiError(error, "Failed to create project");
      throw error;
    }
  }

  /**
   * Update project - TypeScript ensures request type safety
   */
  async updateProject(
    alias: string,
    request: UpdateProjectRequest,
  ): Promise<Project> {
    try {
      const response = await this.makeRequest<Project>(
        ProjectEndpoints.GET_PROJECT.replace(":id", alias),
        "PUT",
        request,
      );

      return response;
    } catch (error) {
      this.handleApiError(error, "Failed to update project");
      throw error;
    }
  }

  /**
   * Delete project
   */
  async deleteProject(alias: string): Promise<void> {
    try {
      await this.makeRequest(
        ProjectEndpoints.GET_PROJECT.replace(":id", alias),
        "DELETE",
      );
    } catch (error) {
      this.handleApiError(error, "Failed to delete project");
      throw error;
    }
  }

  /**
   * Get project applications - TypeScript ensures type safety
   */
  async getProjectApplications(
    projectAlias: string,
    page: number = 1,
    limit: number = 10,
  ): Promise<ApplicationListResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      const response = await this.makeRequest<Application[]>(
        `${ProjectEndpoints.GET_PROJECT_APPLICATIONS.replace(":id", projectAlias)}?${params}`,
        "GET",
      );

      return {
        applications: response,
        total: response.length,
        page,
        limit,
      };
    } catch (error) {
      this.handleApiError(error, "Failed to load project applications");
      throw error;
    }
  }

  /**
   * Get quickstart applications - TypeScript ensures type safety
   */
  async getQuickStartApplications(): Promise<QuickStartApplicationsResponse> {
    try {
      const response = await this.makeRequest<QuickStartApplication[]>(
        ProjectEndpoints.QUICKSTART_APPLICATIONS,
        "GET",
      );

      return {
        applications: response,
        total: response.length,
      };
    } catch (error) {
      this.handleApiError(error, "Failed to load quickstart applications");
      throw error;
    }
  }

  /**
   * Generic request method with error handling
   */
  private async makeRequest<T>(
    endpoint: string,
    method: "GET" | "POST" | "PUT" | "DELETE",
    body?: unknown,
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const options: RequestInit = {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.getAuthToken()}`,
      },
    };

    if (body && method !== "GET") {
      options.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(url, options);

      if (!response.ok) {
        throw await this.createApiError(response);
      }

      const data = await response.json();
      return data as T;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Network error occurred");
    }
  }

  /**
   * Create API error with proper typing
   */
  private async createApiError(response: Response): Promise<ProjectApiError> {
    let errorData: Partial<ProjectApiError> = {};

    try {
      errorData = await response.json();
    } catch {
      // If response is not JSON, create default error
    }

    return {
      code: errorData.code || PROJECT_ERROR_CODES.INTERNAL_ERROR,
      message:
        errorData.message || `HTTP ${response.status}: ${response.statusText}`,
      details: errorData.details,
    };
  }

  /**
   * Handle API errors with event emission
   */
  private handleApiError(error: unknown, defaultMessage: string): void {
    const errorMessage =
      error instanceof Error ? error.message : defaultMessage;

    // Emit error event for logging/monitoring
    this.eventBus.emitGlobal(ProjectEvents.PROJECT_API_ERROR, {
      error: errorMessage,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Get authentication token from storage
   */
  private getAuthToken(): string {
    // This would integrate with your auth system
    return orgSessionManager.getToken() || "";
  }
}

/**
 * Project API factory function
 */
export function createProjectApi(
  eventBus: EventBus,
  baseUrl?: string,
): ProjectApi {
  return new ProjectApi(eventBus, baseUrl);
}
